import {
  afterRender,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  NgZone,
  OnInit,
  signal,
  viewChild,
} from "@angular/core";
import { ScenarioStoreService } from "@bitwarden/web-vault/app/services/store/scenario/scenario.store.service";
import { ScenarioPaths } from "@bitwarden/web-vault/app/models/enum/scenario.types";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { HelperCollapse } from "../../../../shared/utils/helper-collapse";
import { getName } from "../../../../shared/utils/helper-string";
import { HelperExpand } from "../../../../shared/utils/helper-expand";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { GranularityFilter } from "@bitwarden/web-vault/app/gloss/views/views-control/views-customize/granularity.filter";
import { countPeriods } from "@bitwarden/web-vault/app/models/view/transaction/transaction.utils";
import { GranularityProperty } from "@bitwarden/web-vault/app/models/types/balanceGroupingTypes";
// Global, unencapsulated stylesheet: injected via this side-effect import, not Angular's
// styleUrls/styleUrl, so it isn't scoped to this component.
import "./scenario-hud.component.scss";

/** One balance row's side-by-side needs, in px: see balanceRowsFitSideBySide. */
export interface BalanceRowFit {
  /** The row's content width (inside its padding). */
  available: number;
  /**
   * The narrowest the name goes: a path name whole (it never wraps); an account name's longest
   * word, since a longer one wraps between its words beside the amount.
   */
  name: number;
  /** The gap between name and amount. */
  gap: number;
  /** The amount on one line (it never wraps). */
  amount: number;
}

/**
 * Whether every balance row fits its amount beside its name (#6169), the name wrapping between
 * words if it must. One row that doesn't is enough to stack them all, so rows never disagree;
 * otherwise they all stay side by side, however narrow the HUD, as long as these balances fit.
 */
export function balanceRowsFitSideBySide(rows: BalanceRowFit[]): boolean {
  // Half a pixel of slack for sub-pixel text widths.
  return rows.every((row) => row.name + row.gap + row.amount <= row.available + 0.5);
}

@Component({
  selector: "app-scenario-hud",
  templateUrl: "./scenario-hud.component.html",
})
export class ScenarioHudComponent implements OnInit {
  scenarioStoreService = inject(ScenarioStoreService);
  protected helperCollapse: HelperCollapse[];
  protected helperExpand: HelperExpand;
  private userStoreService = inject(UserStoreService);
  protected granularityFilter = inject(GranularityFilter);

  hudIndex = input<number>();

  /** Set when some balance row can't fit name and amount side by side; stacks them all. */
  protected readonly stackBalanceRows = signal(false);
  private readonly pathList = viewChild<ElementRef<HTMLElement>>("pathList");
  private measureCanvas?: CanvasRenderingContext2D | null;
  private lastMeasureKey = "";
  selectedPath = this.scenarioStoreService.currentScenarioType;

  periodCount = computed(() => {
    const granularity = this.granularityFilter.selectedGranularity();
    const { startDate, endDate } = this.scenarioStoreService.period.selected();
    return countPeriods(startDate, endDate, granularity as GranularityProperty);
  });

  periodUnit = computed(() => {
    const granularity = this.granularityFilter.selectedGranularity();
    const count = this.periodCount();
    return count === 1 ? granularity : granularity + "s";
  });

  timeStamp = computed(() => {
    if (this.scenarioStoreService.balanceByTime.balances()) {
      if (
        this.selectedPathIndex() !== undefined &&
        this.scenarioStoreService.balanceByTime.balances()?.[this.selectedPathIndex()]
      ) {
        const balanceByTime = this.scenarioStoreService.balanceByTime.balances();
        const selectedBalance = balanceByTime[this.selectedPathIndex()];
        const keys = [...selectedBalance.keys()];
        return keys[this.hudIndex()];
      }
    }
  });

  balanceDate = computed(() => {
    if (this.timeStamp()) {
      return new Date(Number(this.timeStamp()) * 1000);
    }
  });

  balance = computed(() => {
    const balanceByTime = this.scenarioStoreService.balanceByTime.balances();
    const selectedBalance = balanceByTime[this.selectedPathIndex()];
    return selectedBalance.get(this.timeStamp());
  });

  currency = computed(() => this.userStoreService.preferences.preferenceView()?.coreCurrency);

  pathBalances = computed(() => {
    const pathBalances = [];
    if (this.scenarioStoreService.balanceByTime.balances()) {
      for (const balance of this.scenarioStoreService.balanceByTime.balances()) {
        pathBalances.push(balance.get(this.timeStamp()));
      }
    }
    return pathBalances;
  });

  pathBalanceAccounts = computed(() => {
    const pathBalanceAccounts: { account: string; balance: number; accountID: string }[][] = [];

    if (this.scenarioStoreService.balanceByTimeAndAccounts.balances()) {
      for (const balances of this.scenarioStoreService.balanceByTimeAndAccounts.balances()) {
        const accountBalances = balances.get(this.timeStamp());
        const accountBalancesDisplay: { account: string; balance: number; accountID: string }[] =
          [];

        const accounts = this.userStoreService.enrichAccounts.collection();
        const bestScenario = this.scenarioStoreService.interestScenario.bestScenario();

        if (bestScenario && accounts) {
          accountBalances?.forEach((amount, accountID) => {
            const accountName =
              getName(accounts, accountID) ??
              (getName(bestScenario.createdRecords.accounts, accountID) as string);
            accountBalancesDisplay.push({
              accountID: accountID,
              account: accountName,
              balance: amount,
            });
          });
          pathBalanceAccounts.push(accountBalancesDisplay);
        }
      }
    }
    return pathBalanceAccounts;
  });

  selectedPathIndex = computed(() => ScenarioPaths[this.selectedPath()]);

  constructor() {
    this.helperCollapse = Array.from({ length: 3 }, () => new HelperCollapse());
    this.helperExpand = new HelperExpand();

    this.watchBalanceRowFit();

    // reminder for poor Horace: isCollapsed is when its not expanded
    this.scenarioStoreService.pathTabClicked$.pipe(takeUntilDestroyed()).subscribe((value) => {
      const index = ScenarioPaths[value];
      this.helperCollapse.forEach((helper, i) => {
        helper.isCollapsed = i !== index;
      });
      this.updateHelperExpandedState();
    });
  }

  /**
   * Re-decides stackBalanceRows after a render that changed the rows' text (balances, expanded
   * paths), on a resize of the list, and whenever a web font finishes loading.
   */
  private watchBalanceRowFit(): void {
    const zone = inject(NgZone);
    const remeasure = () => {
      this.lastMeasureKey = "";
      zone.run(() => this.measureBalanceRows());
    };
    // Absent in jsdom (unit tests); renders still re-decide without it.
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(remeasure);
    let observed: HTMLElement | undefined;
    afterRender(() => {
      const list = this.pathList()?.nativeElement;
      if (observer && list && list !== observed) {
        observer.disconnect();
        observer.observe(list);
        observed = list;
      }
      this.measureBalanceRows();
    });
    // A font weight can start loading after fonts.ready has resolved (the first font-medium
    // paint), and changes no text or width when it lands, so listen for every load.
    document.fonts?.addEventListener("loadingdone", remeasure);
    inject(DestroyRef).onDestroy(() => {
      observer?.disconnect();
      document.fonts?.removeEventListener("loadingdone", remeasure);
    });
  }

  private measureBalanceRows(): void {
    const list = this.pathList()?.nativeElement;
    const rows = list ? Array.from(list.querySelectorAll<HTMLElement>(".hud-balance-row")) : [];
    // Text only, which reads no layout: this runs after every render in the app (the chart's
    // hover changes the balances per tick), and the ResizeObserver covers the width.
    const key = rows.map((row) => row.textContent.trim()).join("|");
    if (key === this.lastMeasureKey) {
      return;
    }
    this.lastMeasureKey = key;
    const fits = balanceRowsFitSideBySide(rows.map((row) => this.balanceRowFit(row)));
    if (this.stackBalanceRows() === fits) {
      this.stackBalanceRows.set(!fits);
    }
  }

  private balanceRowFit(row: HTMLElement): BalanceRowFit {
    const [name, amount] = Array.from(row.children) as HTMLElement[];
    const style = getComputedStyle(row);
    return {
      available: row.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight),
      // A path name never wraps ("Path 1"); an account name wraps between its words.
      name: this.textWidth(name, !!name && getComputedStyle(name).whiteSpace !== "nowrap"),
      gap: parseFloat(style.columnGap) || 0,
      amount: this.textWidth(amount),
    };
  }

  /**
   * Width of an element's text on one line in its own font - or, with longestWord, of its widest
   * word, the narrowest it can wrap to.
   */
  private textWidth(element: HTMLElement | undefined, longestWord = false): number {
    if (!element) {
      return 0;
    }
    this.measureCanvas ??= document.createElement("canvas").getContext("2d");
    if (!this.measureCanvas) {
      // No canvas (disabled, a hardened WebView): the laid-out span is already shrunk to fit, so
      // it can't tell. Stacking is the layout that never overlaps.
      return Number.POSITIVE_INFINITY;
    }
    const canvas = this.measureCanvas;
    // From the longhands: the computed `font` shorthand can serialize to "" (older Firefox), which
    // would leave the canvas measuring in its 10px default and never stack.
    const style = getComputedStyle(element);
    canvas.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const text = element.textContent.trim();
    const parts = longestWord ? text.split(/\s+/) : [text];
    return Math.max(0, ...parts.map((part) => canvas.measureText(part).width));
  }

  ngOnInit(): void {
    this.helperCollapse[this.selectedPathIndex()].isCollapsed = false;
    this.updateHelperExpandedState();
  }

  handleExpand(event: any) {
    const isExpanded = this.helperCollapse.every((helper) => !helper.isCollapsed);
    if (isExpanded) {
      this.helperCollapse.forEach((helper) => (helper.isCollapsed = true));
    } else {
      this.helperCollapse.forEach((helper) => (helper.isCollapsed = false));
    }
    this.updateHelperExpandedState();
  }

  updateHelperExpandedState() {
    const isAllHelperExpanded = this.helperCollapse.every((helper) => !helper.isCollapsed);
    this.helperExpand.setExpanded(isAllHelperExpanded);
  }

  handleCollapse(event: any, index: number) {
    this.helperCollapse[index].toggleContent(event);
    this.updateHelperExpandedState();
  }

  getScenarioTypeByIndex(index: number): keyof typeof ScenarioPaths {
    return Object.keys(ScenarioPaths)[index] as keyof typeof ScenarioPaths;
  }

  handleRadioButtonClick(event: any, index: number) {
    const scenarioType = this.getScenarioTypeByIndex(index);
    this.scenarioStoreService.currentScenarioType.set(scenarioType);
    this.helperCollapse[index].isCollapsed = false;
    this.updateHelperExpandedState();
  }
}
